import { Injectable } from '@angular/core';
import { FieldErrors, ProteinCategoriesList, SiteCookies, SiteStorage } from '@constants/global';
import { Cookie } from 'ng2-cookies';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { FormGroup } from '@angular/forms';
import { IInvalidInput, IMsgOptions } from '@interfaces/common';
import { IAccountDetailsData, IPreparedProteinData, IProteinCategories, IProteinCategoriesResponse } from '@interfaces/myaccount';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  constructor(private http: HttpClient) { }

  setCookie(key: SiteCookies, value: string){
    const expireDate = new Date();
    expireDate.setDate(expireDate.getDate() + 7); // Set the cookie expiration date to 7 days
    Cookie.set(key, value, expireDate, '/', environment.cookieDomain , true);
  }

  getCookie(name: SiteCookies){
    return Cookie.get(name);
  }

  deleteTokens(){
    Cookie.delete(SiteCookies.ACCESSTOKEN, '/', environment.cookieDomain);
    Cookie.delete(SiteCookies.REFRESHTOKEN, '/', environment.cookieDomain);
  }

  setInBrowserStorage(key: SiteStorage, value: string){
    sessionStorage.setItem(key, value);
  }

  getFromBrowserStorage(name: SiteStorage){
    return sessionStorage.getItem(name);
  }

  deleteFromBrowserStorage(name: SiteStorage){
    sessionStorage.removeItem(name);
  }

  private _getDefaultHttpHeaders(){
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  }

  isObjectEmpty(obj: {[key: string]: string} ){
    const objLength = Object.keys(obj).length;
    let flag = false;
    if(objLength == 0){
      flag = true;
    }
    return flag;
  }

  sendRequest<R>(url: string, payload?: any, headers?: any, queryParams?: any){
    /* if(window.location.href.indexOf('localhost') > 0){
      payload = {};
      queryParams = {};
    } */
    const httpOptions = {
      headers: new HttpHeaders({...this._getDefaultHttpHeaders(), ...headers}),
      params: new HttpParams()
    };
    if(queryParams && !this.isObjectEmpty(queryParams)){
      for(let k in queryParams){
        httpOptions.params = httpOptions.params.set(k, queryParams[k]);
      }
    }
    let response;
    if(payload && !this.isObjectEmpty(payload)){
      response = this.http.post<R>(url, payload, httpOptions);
    }else{
      response = this.http.get<R>(url, httpOptions);
    }
    return response;
  }

  filterInvalidInputs(formObj: FormGroup){
    let keys = Object.keys(formObj.value),
        errorTxt = '',
        invalidInputs: Array<IInvalidInput> = [];
    keys.forEach((v) => {
      if(!formObj.controls[v].valid){
        const errorObj = formObj.controls[v].errors;
        if(errorObj?.required){
          errorTxt = FieldErrors[v].required;
        }else if(errorObj?.pattern){
          errorTxt = FieldErrors[v].pattern;
        }else if(errorObj?.mismatch){
          errorTxt = FieldErrors[v].mismatch;
        }else if(errorObj?.invalid){
          errorTxt = FieldErrors[v].invalid;
        }else if(errorObj?.maxlength){
          errorTxt = FieldErrors[v].maxlength;
        }
        invalidInputs.push({
          'field': v,
          'error': errorTxt
        });
      }else{
        invalidInputs.push({
          'field': v,
          'error': ''
        });
      }
    });
    return invalidInputs;
  }

  /**
   * Function to detect mobile
   */
  isMobile = () => {
    let flag = false;
    if(document.documentElement.clientWidth <= 767){
      flag = true;
    }
    return flag;
  }

  getMsgOptionsObj(type: string, details: string | undefined): IMsgOptions{
    const messageOptions: IMsgOptions = {
      messageType: type,
      messageDetails: details ? '<ul>'+details+'</ul>' : ''
    };

    return {...messageOptions};
  }

  /**
   * Maps the service protein categories with the static protein list and splits them into avoided / included proteins.
   * The prepared data is also set on the passed account data so it can be passed to the meal preference modal.
   * @param accountData : account details data
   * @param proteinCategories : protein categories returned by the service (defaults to the ones present in account data)
   */
  prepareProteinCategories(accountData: IAccountDetailsData, proteinCategories?: Array<IProteinCategoriesResponse>): IPreparedProteinData{
    const proteinCategoriesArray: Array<IProteinCategories> = JSON.parse(JSON.stringify(ProteinCategoriesList));
    const proteinCategoriesResponse: Array<IProteinCategoriesResponse> = proteinCategories || accountData.protein_categories || [];

    proteinCategoriesArray.forEach((v) => {
      const mappedProteinObj = proteinCategoriesResponse.filter((item) => item.slug.includes(v.slug))[0];
      if(mappedProteinObj){
        v.id = mappedProteinObj.id;
      }
    });

    let avoidProteinsArr: Array<string> = [],
        avoidProteinIdsArr: Array<number> = [],
        includeProteinsArr: Array<string> = [],
        includeProteinIdsArr: Array<number> = [];
    proteinCategoriesArray.forEach((v) => {
      if(accountData.avoid_proteins?.includes(v.id.toString())){
        avoidProteinsArr.push(v.name);
        avoidProteinIdsArr.push(v.id);
      }else{
        includeProteinsArr.push(v.name);
        includeProteinIdsArr.push(v.id);
      }
    });

    accountData.proteinCategoriesArray = proteinCategoriesArray;
    accountData.avoidProteinIdsArr = avoidProteinIdsArr;
    accountData.includeProteinIdsArr = includeProteinIdsArr;

    return {
      proteinCategoriesArray,
      avoidProteinIdsArr,
      includeProteinIdsArr,
      avoidProteins: avoidProteinsArr.toString(),
      includeProteins: includeProteinsArr.toString()
    };
  }

  /* getFormattedDate(timestamp: number, need_year_display: boolean = false) {
    const _d = new Date(timestamp * 1000);

    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const day = _d.getDate();
    let nthL = "th";
    const nth = function(day: number) {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:  return "st";
        case 2:  return "nd";
        case 3:  return "rd";
        default: return "th";
      }
    }
    if ( need_year_display){
        return months[_d.getMonth()] + " " + day + nth(day) + ", " + _d.getFullYear() ;
    }
    return months[_d.getMonth()] + " " + day + nth(day) ;
  } */

  displayMonthTextDayYear(date:string, need_year_display: boolean = false){
    let dateStr = date.toString().split('-');
    const day = parseInt(dateStr[0]);
    const month = parseInt(dateStr[1]);
    const year = parseInt(dateStr[2]);
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  
    const nth = function(day: number) {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:  return "st";
        case 2:  return "nd";
        case 3:  return "rd";
        default: return "th";
      }
    }
    if ( need_year_display){
        return months[month - 1] + " " + day + nth(day) + ", " + year ;
    }
    return months[month - 1] + " " + day + nth(day) ;
  }

  scrollToTop(){
    setTimeout(() => {
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    }, 0);
  }
}
